import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import ProtectedRoute from "./auth/ProtectedRoute";
import { HosProvider } from "./hos/hosStore";
import NetworkActivity from "./components/NetworkActivity";
import { GlobalRuntimeErrorBanner, RouteErrorBoundary } from "./components/AppErrorBoundary";
import { ConfirmDialogHost } from "./components/ConfirmDialogHost";
import { HosBootLoader } from "./components/hos/HosBootLoader";
import { HOS_SHELL_ENABLED } from "./components/hos/shell/hosShellFlag";
import { useTranslation } from "react-i18next";

const ModuleShell = lazy(() => import("./screens/ModuleShell"));
const NotFound = lazy(() => import("./screens/NotFound"));
const SettingsShell = lazy(() => import("./screens/settings/SettingsShell"));
const Login = lazy(() => import("./screens/Auth/Login"));
const Mfa = lazy(() => import("./screens/Auth/Mfa"));
const AuthCallback = lazy(() => import("./screens/Auth/AuthCallback"));
const PublicGroupBooking = lazy(() => import("./screens/PublicGroupBooking/PublicGroupBooking"));
const PublicGroupRoomingList = lazy(() => import("./screens/PublicGroupRoomingList/PublicGroupRoomingList"));
const PublicDocumentSignature = lazy(() => import("./screens/PublicDocumentSignature/PublicDocumentSignature"));
const OpsPmsRouteScope = lazy(() => import("./screens/PMS/OpsPmsRouteScope"));
const EmbedRoomAdministration = lazy(() => import("./screens/Embed/EmbedRoomAdministration"));
const HosShellLayout = lazy(() => import("./components/hos/shell/HosShellLayout"));
const XentrosDashboardWorkspace = lazy(() =>
  import("./screens/PMS/PMS.Host").then((module) => ({ default: module.XentrosDashboardWorkspace }))
);
// DESIGN.md v2 component catalogue. Dev builds only: Vite replaces import.meta.env.DEV with false
// in production, so the route and its chunk are dropped from the release bundle.
const HosUiPreview = import.meta.env.DEV ? lazy(() => import("./screens/Dev/HosUiPreview")) : null;

function AppLoading() {
  const { t } = useTranslation();
  return <HosBootLoader label={t("pms.common.state.loading", { defaultValue: "Loading…" })} />;
}

function LegacyIdentityRedirect() {
  const location = useLocation();
  const path = location.pathname;

  if (path.startsWith("/settings/identity/roles") || path.startsWith("/settings/roles")) {
    return <Navigate to="/module/identity/roles" replace />;
  }

  return <Navigate to="/module/identity" replace />;
}

export default function App() {
  return (
    <HosProvider>
      <NetworkActivity />
      <GlobalRuntimeErrorBanner />
      <ConfirmDialogHost />
      <RouteErrorBoundary>
        <Suspense fallback={<AppLoading />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/mfa" element={<Mfa />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/book/groups/:token" element={<PublicGroupBooking />} />
            <Route path="/group-rooming-list/:token" element={<PublicGroupRoomingList />} />
            <Route path="/sign/documents/:token" element={<PublicDocumentSignature />} />
            <Route path="/embed/rooms" element={<EmbedRoomAdministration />} />
            {HosUiPreview ? <Route path="/dev/hos-ui" element={<HosUiPreview />} /> : null}

            {/* XEN-92: the Infoboard and the modules share one layout so the
                PMS data provider is not remounted between / and /module/pms.
                New shell: the sidebar and top bar live in that layout too, so
                moving between modules only swaps the content area. */}
            <Route
              element={
                <ProtectedRoute>
                  {HOS_SHELL_ENABLED ? (
                    <HosShellLayout>
                      <OpsPmsRouteScope />
                    </HosShellLayout>
                  ) : (
                    <OpsPmsRouteScope />
                  )}
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<XentrosDashboardWorkspace />} />
              <Route path="/module/:key/*" element={<ModuleShell />} />
            </Route>

            <Route
              path="/settings/identity/*"
              element={
                <ProtectedRoute>
                  <LegacyIdentityRedirect />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/roles"
              element={
                <ProtectedRoute>
                  <LegacyIdentityRedirect />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/*"
              element={
                <ProtectedRoute>
                  <SettingsShell />
                </ProtectedRoute>
              }
            />

            {/* XEN-281 (C-29): an unknown address says so instead of silently
                rendering the Infoboard, which made a typo or a stale bookmark
                look like it had worked. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </HosProvider>
  );
}
